/**
 * PROTOTYPE ONLY.
 *
 * Variant B — "One page, collapsing". Rejects the multi-step premise. Every section
 * is a panel on a single page; finishing one collapses it to a summary row, so the
 * page turns into Check your answers on its own rather than needing a separate screen
 * for it. Creation and customisation are the same page: after Create, the three
 * booking-experience panels unlock in place and the page becomes the edit surface.
 *
 * The bet being tested: is a separate review step, and a separate success screen,
 * carrying its weight?
 */
import {useState} from "react";
import {Badge, Button, Card, Collapse, Divider, Space, Typography} from "antd";
import {
  Customisation,
  DraftSetter,
  ImportanceTag,
  SECTIONS,
  SectionFields,
  SectionKey,
  SessionDraft,
  customisationItems,
  sectionSummary,
} from "./shared";
import {applyCustomisation} from "./variant-a-wizard";

const {Title, Text, Paragraph} = Typography;

interface VariantProps {
  draft: SessionDraft;
  set: DraftSetter;
  customisation: Customisation;
  setCustomisation: (next: Customisation) => void;
}

export function VariantBAccordion({
  draft,
  set,
  customisation,
  setCustomisation,
}: VariantProps) {
  const [openPanel, setOpenPanel] = useState<string>("general");
  const [done, setDone] = useState<SectionKey[]>([]);
  const [created, setCreated] = useState(false);

  function markDone(section: SectionKey) {
    setDone((current) => (current.includes(section) ? current : [...current, section]));
    const next = SECTIONS.find(
      (entry) => entry.key !== section && !done.includes(entry.key)
    );
    setOpenPanel(next ? next.key : "");
  }

  const allDone = SECTIONS.every((section) => done.includes(section.key));

  return (
    <div style={{paddingBottom: 96}}>
      <Title level={4} style={{marginTop: 0}}>
        {created ? draft.name || "Your session" : "Create a 1:1 session"}
      </Title>
      <Paragraph type="secondary">
        {created
          ? "This session is live and accepting bookings. Everything below is editable in place."
          : "Fill in each section. Sections collapse into a summary as you finish them, so the page doubles as your final check."}
      </Paragraph>

      <Collapse
        accordion
        activeKey={openPanel}
        onChange={(key) => setOpenPanel(Array.isArray(key) ? key[0] ?? "" : key ?? "")}
      >
        {SECTIONS.map((section) => {
          const isDone = done.includes(section.key);

          return (
            <Collapse.Panel
              key={section.key}
              header={
                <Space>
                  {isDone ? <Badge status="success" /> : <Badge status="default" />}
                  <Text strong>{section.title}</Text>
                  {isDone ? (
                    <Text type="secondary" style={{fontSize: 12}}>
                      {sectionSummary(section.key, draft).join(" · ")}
                    </Text>
                  ) : null}
                </Space>
              }
            >
              <SectionFields section={section.key} draft={draft} set={set} />
              <Divider />
              <Button type="primary" onClick={() => markDone(section.key)}>
                {isDone ? "Update" : "Save section"}
              </Button>
            </Collapse.Panel>
          );
        })}
      </Collapse>

      {created ? (
        <>
          <Title level={5} style={{marginTop: 24}}>
            Booking experience
          </Title>
          <Collapse>
            {customisationItems(customisation).map((item) => (
              <Collapse.Panel
                key={item.key}
                header={
                  <Space>
                    <Text strong>{item.title}</Text>
                    <ImportanceTag level={item.level} />
                    <Text type="secondary" style={{fontSize: 12}}>
                      {item.status}
                    </Text>
                  </Space>
                }
              >
                <Paragraph type="secondary">{item.blurb}</Paragraph>
                <Button
                  onClick={() =>
                    setCustomisation(applyCustomisation(customisation, item.key))
                  }
                >
                  {item.key === "coverImage" ? "Add cover image" : "Set up"}
                </Button>
              </Collapse.Panel>
            ))}
          </Collapse>
        </>
      ) : null}

      {!created ? (
        <Card
          size="small"
          style={{
            position: "sticky",
            bottom: 16,
            marginTop: 16,
            boxShadow: "0 -4px 16px rgba(0,0,0,0.06)",
          }}
        >
          <div style={{display: "flex", justifyContent: "space-between", gap: 16}}>
            <Text type="secondary">
              {done.length} of {SECTIONS.length} sections done
            </Text>
            <Button type="primary" disabled={!allDone} onClick={() => setCreated(true)}>
              Create session
            </Button>
          </div>
        </Card>
      ) : null}
    </div>
  );
}
